import React from 'react'
import i18next from 'i18next'
import {USER_GUIDE_SELECTORS} from '@/constants'
import {ROUTES} from '@/router/routes'
import {UserGuideStep} from '@/stores/user-guide/models/UserGuideStep'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'
import {hasPermissions} from '@/stores/auth/services'
import {isMobile} from '@/utils'
import {COMPANY_NAME} from '@/constants/app'

export const getSteps = (isVirtualTerminalAvailable: boolean, onSkipClick?): UserGuideStep[] => {
    const steps = [
        {
            title: i18next.t('globals:pages.overview'),
            content: i18next.t('user-guide.overviewDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.overview}`,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            link: ROUTES.home,
            permission: [PermissionsMap.overview.read]
        },
        {
            title: i18next.t('globals:pages.posPayments'),
            content: i18next.t('user-guide.posPaymentsDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.posPayments}`,
            link: ROUTES.posPayments,
            permission: [PermissionsMap.pos_payments.full, PermissionsMap.pos_payments.read]
        },
        {
            title: i18next.t('globals:pages.onlinePayments'),
            content: i18next.t('user-guide.onlinePaymentsDescriptions'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.onlinePayments}`,
            link: ROUTES.onlinePayments,
            permission: [PermissionsMap.online_payments.full, PermissionsMap.online_payments.read]
        },
        {
            title: i18next.t('globals:pages.paymentLinks'),
            content: i18next.t('user-guide.paymentLinksDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: `.${USER_GUIDE_SELECTORS.paymentLinks}`,
            link: ROUTES.paymentLinks,
            permission: [PermissionsMap.payment_links.full, PermissionsMap.payment_links.read]
        },
        {
            title: i18next.t('globals:pages.paymentLinks'),
            content: i18next.t('user-guide.paymentLinksCreate'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            target: `.${USER_GUIDE_SELECTORS.createNewPaymentLink}`,
            permission: [PermissionsMap.payment_links.full]
        },
        {
            title: i18next.t('globals:pages.virtualTerminal'),
            content: i18next.t('user-guide.virtualTerminalDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.virtualTerminal}`,
            link: ROUTES.virtualTerminal,
            permission: [PermissionsMap.virtual_terminal.full]
        },
        {
            title: i18next.t('globals:pages.virtualTerminal'),
            content: i18next.t('user-guide.virtualTerminalDetails'),
            locale: {
                skip: <strong aria-label='skip'>{i18next.t('user-guide.skipTutorial')}</strong>
            },
            disableBeacon: true,
            target: `.${USER_GUIDE_SELECTORS.virtualTerminalDetails}`,
            permission: [PermissionsMap.virtual_terminal.full]
        },
        {
            title: i18next.t('globals:pages.posAmexPayments'),
            content: i18next.t('user-guide.posAmexPaymentsDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.posAmexPayments}`,
            link: ROUTES.posAmexPayments,
            permission: [
                PermissionsMap.pos_amex_payments.full,
                PermissionsMap.pos_amex_payments.read
            ]
        },
        {
            title: i18next.t('globals:pages.chargebacks'),
            content: i18next.t('user-guide.chargebacksDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.chargebacks}`,
            link: ROUTES.chargebacks,
            permission: [PermissionsMap.chargebacks.read]
        },
        {
            title: i18next.t('globals:pages.settlements'),
            content: i18next.t('user-guide.settlementsDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.settlements}`,
            link: ROUTES.settlements.perTransactionDate,
            permission: [PermissionsMap.settlements.full, PermissionsMap.settlements.read]
        },
        {
            title: i18next.t('globals:pages.settlements'),
            content: i18next.t('user-guide.settlementsDownload'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            target: `.${USER_GUIDE_SELECTORS.settlementsDownload}`,
            permission: [PermissionsMap.settlements.full]
        },
        {
            title: i18next.t('globals:pages.dcc'),
            content: i18next.t('user-guide.dccDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.dcc}`,
            link: ROUTES.dcc,
            permission: [PermissionsMap.dcc.read]
        },
        {
            title: i18next.t('globals:pages.zashEpos'),
            content: i18next.t('user-guide.zashEposDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: !!isMobile.any() ? 'body' : `.${USER_GUIDE_SELECTORS.zashEpos}`,
            link: ROUTES.zashEpos,
            permission: [PermissionsMap.zash_epos.full]
        },
        {
            title: i18next.t('globals:pages.invoices'),
            content: i18next.t('user-guide.invoicesDescription', {companyName: COMPANY_NAME}),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: `.${USER_GUIDE_SELECTORS.invoices}`,
            link: ROUTES.billing.invoices,
            permission: [PermissionsMap.invoices.full, PermissionsMap.invoices.read]
        },
        {
            title: i18next.t('globals:pages.reports'),
            content: i18next.t('user-guide.reportsDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: `.${USER_GUIDE_SELECTORS.reports}`,
            link: ROUTES.reports,
            permission: [PermissionsMap.reports.read]
        },
        {
            title: i18next.t('globals:pages.exports'),
            content: i18next.t('user-guide.exportDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: `.${USER_GUIDE_SELECTORS.exports}`,
            link: ROUTES.exports,
            permission: [
                PermissionsMap.pos_payments.read,
                PermissionsMap.pos_payments.full,
                PermissionsMap.pos_amex_payments.read,
                PermissionsMap.pos_amex_payments.full,
                PermissionsMap.online_payments.read,
                PermissionsMap.online_payments.full,
                PermissionsMap.settlements.full
            ]
        },
        {
            title: i18next.t('globals:pages.settings'),
            content: i18next.t('user-guide.settingsDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: `.${USER_GUIDE_SELECTORS.settings}`,
            link: ROUTES.settings.base,
            permission: [PermissionsMap.payment_methods.full, PermissionsMap.payment_methods.read]
        },
        {
            title: i18next.t('globals:pages.paymentMethods'),
            content: i18next.t('user-guide.paymentMethodsDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: `.${USER_GUIDE_SELECTORS.paymentMethodsCard}`,
            permission: [PermissionsMap.settlements.full]
        },
        {
            title: i18next.t('globals:pages.teamManagement'),
            content: i18next.t('user-guide.teamManagementDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: `.${USER_GUIDE_SELECTORS.teamManagementCard}`,
            permission: [PermissionsMap.teammates.full, PermissionsMap.teammates.read]
        },
        {
            title: i18next.t('globals:pages.teamManagement'),
            content: i18next.t('user-guide.teamManagementAddUserDescription'),
            locale: {
                skip: (
                    <strong aria-label='skip' onClick={onSkipClick}>
                        {i18next.t('user-guide.skipTutorial')}
                    </strong>
                )
            },
            disableBeacon: true,
            placement: !!isMobile.any() ? 'center' : 'right-start',
            target: `.${USER_GUIDE_SELECTORS.teamManagementButton}`,
            link: ROUTES.settings.teamManagement,
            permission: [PermissionsMap.teammates.full]
        },
        {
            title: i18next.t('user-guide.thankYou'),
            content: i18next.t('user-guide.thankYouDescription', {companyName: COMPANY_NAME}),
            disableBeacon: true,
            placement: 'center',
            target: 'body',
            permission: [PermissionsMap.overview.read]
        }
    ]

    const filteredSteps = steps
        .filter((step) => {
            if (
                step.target === `.${USER_GUIDE_SELECTORS.virtualTerminalDetails}` ||
                step.link === ROUTES.virtualTerminal
            ) {
                return isVirtualTerminalAvailable && hasPermissions(step.permission)
            }
            return hasPermissions(step.permission)
        })
        .map((step, index) => ({...step, step: index}))

    return filteredSteps as UserGuideStep[]
}
